<template>
  <div class="search-and-actions-container">
    <!-- Search Bar -->
    <!-- WCAG 1.3.6: role="search" marks this as the search landmark -->
    <div class="search-bar-maker-cart" role="search">
      <!-- Decorative icon: aria-hidden keeps it out of AT (WCAG 1.1.1) -->
      <span class="search-icon" aria-hidden="true">
        <SearchTonedIcon width="24" height="24" color="#6C737F" />
      </span>
      <!-- WCAG 1.3.1 / 4.1.2: aria-label provides an accessible name since there is no visible <label> -->
      <input
        id="makercart-search-input"
        class="input-main"
        type="search"
        v-model="searchQuery"
        @input="handleSearchInput"
        :aria-label="ariaLabel"
        :placeholder="placeholder"
      />
      <!-- WCAG 4.1.2 / 2.1.1: button element is keyboard-accessible -->
      <button
        v-if="searchQuery"
        type="button"
        class="remove-icon"
        aria-label="Clear search"
        @click="clearSearch"
      >
        <img src="/images/close.svg" alt="" aria-hidden="true" />
      </button>
      <!-- Visual label beside the input — decorative, already conveyed by aria-label (WCAG 1.3.1) -->
      <div class="search-text-container" aria-hidden="true">
        <div class="search-text">Search</div>
      </div>
    </div>

    <!-- Actions Section (Locale + Cart) -->
    <div v-if="!hideLocale" class="actions-section">
      <!-- Locale Selector -->
      <LocaleSelector
        v-model="selectedLocale"
        @update:modelValue="handleLocaleChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from "vue";
import LocaleSelector from "@/components/common/LocaleSelector.vue";
import SearchTonedIcon from "@/components/icons/SearchTonedIcon.vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  locale: {
    type: String,
    default: "US",
  },
  hideLocale: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default:
      "Search activities by grade, subject, or keyword",
  },
  ariaLabel: {
    type: String,
    default: "Search Makercart: topic, hands-on activity, or any idea",
  },
});

const emit = defineEmits([
  "update:modelValue",
  "search",
  "clear-search",
  "locale-changed",
  "cart-clicked",
]);

// Local reactive state
const searchQuery = ref(props.modelValue);
const selectedLocale = ref(props.locale);

// Sync with props
watch(
  () => props.modelValue,
  (newValue) => {
    searchQuery.value = newValue;
  }
);

watch(
  () => props.locale,
  (newValue) => {
    selectedLocale.value = newValue;
  }
);

const handleSearchInput = () => {
  emit("update:modelValue", searchQuery.value);
  emit("search", searchQuery.value);
};

const clearSearch = () => {
  searchQuery.value = "";
  emit("update:modelValue", "");
  emit("clear-search");
};

const handleLocaleChange = (locale) => {
  selectedLocale.value = locale;
  emit("locale-changed", locale);
};
</script>

<style scoped>
.search-and-actions-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  width: 100%;
  height: 56px;
}

.search-bar-maker-cart {
  display: flex;
  align-items: center;
  position: relative;
  flex: 1;
  height: 100%;
}

.search-icon {
  position: absolute;
  left: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  width: 24px;
  height: 24px;
}

.search-bar-maker-cart input {
  flex: 1;
  padding: 4px 130px 4px 56px !important;
  background-color: var(--white, #ffffff);
  border: 1px solid var(--neutral-700);
  border-radius: 8px;
  font-size: 20px;
  font-family: 'League Spartan', sans-serif;
  color: var(--neutral-700);
  outline: 0;
  width: 100%;
  height: 100%;
  letter-spacing: 0.03px;
  transition: all 0.2s ease;
}

.search-bar-maker-cart:focus-within input {
  border-color: var(--primary-blue);
  background-color: var(--white-hover-1);
}

.search-bar-maker-cart input::placeholder {
  color: var(--neutral-700);
  opacity: 1;
}

.remove-icon {
  position: absolute;
  right: 110px; /* adjusted to account for the wider search text block */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  /* Reset native <button> styles (WCAG 4.1.2) */
  background: none;
  border: none;
  padding: 0;
}

/* Visible focus ring for keyboard users (WCAG 2.4.7) */
.remove-icon:focus-visible {
  outline: 3px solid var(--primary-blue);
  outline-offset: 2px;
  border-radius: 4px;
}

.remove-icon img {
  width: 18px;
  height: 18px;
}

.search-text-container {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  height: 100%;
  background-color: var(--blue-light-1, #00599a);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 8px 8px 0;
  z-index: 2;
  pointer-events: none;
}

.search-text {
  font-size: 20px;
  font-family: 'League Spartan', sans-serif;
  font-weight: 500;
  letter-spacing: 0.03px;
  color: #ffffff;
  padding: 0 20px;
}

.actions-section {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  height: 100%;
}

.cart-button {
  position: relative;
  padding: 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.cart-button:hover {
  background-color: rgba(0, 61, 106, 0.1);
}

/* Mobile responsive */
@media (max-width: 768px) {
  .search-and-actions-container {
    flex-direction: column;
    gap: 15px;
  }

  .search-bar-maker-cart {
    max-width: none;
  }

  .actions-section {
    align-self: flex-end;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .search-and-actions-container {
    gap: 12px;
  }

  .cart-button {
    padding: 6px;
  }
  
  .search-bar-maker-cart input {
    font-size: 16px;
    padding: 4px 120px 4px 44px !important;
  }
  
  .search-icon {
    left: 12px;
  }
}
</style>
